import createClient from "openapi-fetch";
import type { paths } from "./schema";

/**
 * Typed API client generated from the backend's OpenAPI schema.
 *
 * `npm run gen:api` regenerates `schema.d.ts` from `backend/openapi.json`, and
 * CI fails if the committed schema has drifted from the running application —
 * so an endpoint change surfaces here as a TypeScript error rather than a
 * runtime surprise.
 */
export const api = createClient<paths>({ baseUrl: "/" });

export interface ApiErrorBody {
  error: { code: string; message: string; detail?: unknown };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as ApiErrorBody).error?.code === "string"
  );
}

function fail(result: { error?: unknown; response: Response }): never {
  if (isErrorBody(result.error)) {
    const { code, message, detail } = result.error.error;
    throw new ApiError(result.response.status, code, message, detail);
  }
  throw new ApiError(
    result.response.status,
    "request_failed",
    `Request failed with status ${result.response.status}.`,
  );
}

/** Turn openapi-fetch's `{ data, error }` result into a value or a thrown ApiError. */
export function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.error !== undefined || result.data === undefined) fail(result);
  return result.data;
}

/**
 * The same, for an endpoint whose success carries no body.
 *
 * Every DELETE in this API answers `204 No Content`, and openapi-fetch reports
 * that as `{ data: undefined }` — which `unwrap` cannot tell apart from a
 * request that failed, so it threw on success. The mutations therefore never
 * reached `onSuccess`, never invalidated anything, and the deleted row sat
 * there until the page was reloaded; nothing rendered the error, so it read as
 * a delete that quietly did nothing.
 *
 * Kept separate rather than letting `unwrap` return `undefined as T`: an
 * endpoint that is *supposed* to return a body and does not should still be
 * loud, and it should be loud there rather than as a crash further downstream.
 */
export function unwrapEmpty(result: { error?: unknown; response: Response }): void {
  if (result.response.ok) return;
  fail(result);
}

/** Seconds to wait before retrying, as reported by a rate-limited response. */
export function retryAfterSeconds(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.code !== "rate_limited") return null;
  const detail = error.detail;
  if (typeof detail === "object" && detail !== null && "retry_after_seconds" in detail) {
    const value = (detail as { retry_after_seconds: unknown }).retry_after_seconds;
    if (typeof value === "number") return value;
  }
  return null;
}


/**
 * Upload a CSV/TSV file into a managed source (PRD FR-1.1).
 *
 * Multipart bodies bypass the generated client on purpose: `openapi-fetch`
 * would need a custom serialiser for `FormData`, and this way the browser sets
 * its own boundary. The response is still parsed through the same error
 * envelope, so callers see an `ApiError` like everywhere else.
 */
export async function uploadImport(
  connectionId: string,
  file: File,
  options: { tableName?: string; mode?: string; hasHeader?: boolean; delimiter?: string },
): Promise<unknown> {
  const body = new FormData();
  body.append("file", file);
  if (options.tableName) body.append("table_name", options.tableName);
  if (options.mode) body.append("mode", options.mode);
  if (options.delimiter) body.append("delimiter", options.delimiter);
  body.append("has_header", String(options.hasHeader ?? true));

  const response = await fetch(`/api/connections/${encodeURIComponent(connectionId)}/imports`, {
    method: "POST",
    body,
    credentials: "same-origin",
  });
  const payload: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    if (isErrorBody(payload)) {
      const { code, message, detail } = payload.error;
      throw new ApiError(response.status, code, message, detail);
    }
    throw new ApiError(response.status, "import_failed", `Upload failed (${response.status}).`);
  }
  return payload;
}

/** How far an upload has got, 0–1. */
export type UploadProgress = (fraction: number) => void;

/**
 * Send a file as the request body (FILE-2): `POST /api/connections/upload`
 * to make a source of it, `PUT /api/connections/{id}/file` to replace one.
 *
 * XHR rather than `fetch` because only XHR reports *upload* progress, and a
 * 400 MB workbook with no progress bar looks exactly like a hung page. The
 * body is the file itself, not multipart — the server streams it straight
 * onto the files volume and refuses an oversized one from `Content-Length`
 * before reading it.
 */
export function sendFile<T>(
  method: "POST" | "PUT",
  path: string,
  file: File,
  params: Record<string, string | undefined>,
  onProgress?: UploadProgress,
): Promise<T> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value);
  }
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(method, `${path}?${query.toString()}`);
    request.withCredentials = true;
    request.setRequestHeader("Content-Type", "application/octet-stream");
    request.responseType = "json";
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    };
    request.onerror = () =>
      reject(new ApiError(0, "network_error", "The upload did not reach the server."));
    request.onload = () => {
      const payload: unknown = request.response;
      if (request.status >= 200 && request.status < 300) {
        resolve(payload as T);
        return;
      }
      if (isErrorBody(payload)) {
        const { code, message, detail } = payload.error;
        reject(new ApiError(request.status, code, message, detail));
        return;
      }
      reject(new ApiError(request.status, "upload_failed", `Upload failed (${request.status}).`));
    };
    request.send(file);
  });
}
